import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, X } from 'lucide-react'

interface Props {
  url: string | null
  alt?: string
  onClose: () => void
}

/**
 * Lightbox plein écran pour les images. Click background → close, Esc → close,
 * click sur l'image elle-même → no-op (stopPropagation).
 *
 * Bouton Télécharger en haut à droite (target=_blank pour ne pas voler le tab
 * actif si l'user a juste cliqué pour preview).
 *
 * Utilisé par SupportWidget (inline version, pas encore migré) et par
 * ConversationView pour les médias MMS reçus/envoyés.
 */
export function Lightbox({ url, alt, onClose }: Props) {
  const { t } = useTranslation()

  useEffect(() => {
    if (!url) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [url, onClose])

  if (!url) return null

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[100] flex animate-in fade-in items-center justify-center bg-black/90 p-4 duration-150"
    >
      <div
        className="absolute right-4 top-4 z-10 flex items-center gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        <a
          href={url}
          download
          target="_blank"
          rel="noreferrer"
          className="rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20"
          aria-label={t('lightbox.download', 'Télécharger')}
          title={t('lightbox.download', 'Télécharger')}
        >
          <Download className="h-4 w-4" />
        </a>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20"
          aria-label={t('lightbox.close', 'Fermer (Échap)')}
          title={t('lightbox.close', 'Fermer (Échap)')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <img
        src={url}
        alt={alt || t('lightbox.preview', 'Aperçu')}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[95vh] max-w-[95vw] object-contain"
      />
    </div>
  )
}
